// Any other device, drawn the way Live draws a device without a custom
// view: its parameters as knobs, switches and step selects, in the Set's
// order. Every one of them travels — knobs here move Live's own.

import type { DeviceParam } from "@shared/sync/state.js";
import { cn } from "@web-shared/lib/utils";
import { Knob, StepSelect, Toggle } from "@web-shared/components/devices/controls";
import type { EditorProps } from "@web-shared/components/devices/types";

/** Enough digits to tell neighbouring values apart, no more. */
function fmt(p: DeviceParam): (v: number) => string {
  const span = Math.abs(p.max - p.min);
  const digits = p.quantized ? 0 : span >= 100 ? 0 : span >= 10 ? 1 : 2;
  return (v) => v.toFixed(digits);
}

function isSwitch(p: DeviceParam): boolean {
  return p.quantized === true && p.max - p.min === 1 && (!p.items || p.items.length <= 2) && !(p.items && p.items.length === 2 && !/^(on|off)$/i.test(p.items[1]!));
}

function logish(p: DeviceParam): boolean {
  return p.min > 0 && p.max / p.min >= 100 && /freq|time|rate|hz/i.test(p.name);
}

export function GenericDevice({ device, disabled, set }: EditorProps) {
  const shown = device.params.map((p, i) => ({ p, i })).filter(({ p }) => p.name !== "Device On");
  if (shown.length === 0) return <div className="text-[10px] text-muted-foreground">No parameters.</div>;
  return (
    <div className={cn("flex max-h-[176px] flex-col flex-wrap content-start gap-x-1 gap-y-1")} data-testid="generic-device">
      {shown.map(({ p, i }) =>
        isSwitch(p) ? (
          <div key={i} className="flex h-[56px] w-[52px] flex-col items-center justify-center gap-0.5">
            <span className="w-full truncate text-center text-[9px] text-muted-foreground" title={p.name}>
              {p.name}
            </span>
            <Toggle label={p.value > p.min ? "On" : "Off"} on={p.value > p.min} disabled={disabled} onChange={(on) => set(i, on ? p.max : p.min, true)} title={p.name} />
          </div>
        ) : p.quantized && p.items && p.items.length > 0 ? (
          <div key={i} className="flex h-[56px] w-[104px] items-center">
            <StepSelect label={p.name} value={Math.round(p.value - p.min)} options={p.items.map((label, k) => ({ value: k, label }))} disabled={disabled} onChange={(k) => set(i, p.min + k, true)} />
          </div>
        ) : (
          <Knob
            key={i}
            label={p.name}
            value={p.value}
            min={Math.min(p.min, p.max)}
            max={Math.max(p.min, p.max)}
            log={logish(p)}
            step={p.quantized ? 1 : undefined}
            bipolar={p.min < 0 && p.max > 0}
            format={fmt(p)}
            disabled={disabled}
            onDraft={(v) => set(i, v, false)}
            onCommit={(v) => set(i, v, true)}
          />
        ),
      )}
    </div>
  );
}
